import {
  Container,
  Card,
  Typography,
  LinearProgress,
  Avatar,
  Box,
  Grid2 as Grid,
  Stack,
} from "@mui/material";
import { useParams } from "react-router";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCustom } from "@refinedev/core";
import { FileDownload } from "@mui/icons-material";
import { useMemo } from "react";
import { CarrierServicesApiResponse } from "../listing/types";
import { PriceList } from "./components/PriceList";
import LoadingButton from "@mui/lab/LoadingButton";

export const ShipmentPriceDetails = () => {
  const { translate } = useTranslation("pages.shipmentPrices.details");

  const { translate: translateShipmentFields } = useTranslation(
    "pages.shipmentPrices.fields"
  );

  const { countryId, carrierId, serviceId } = useParams<{
    countryId: string;
    carrierId: string;
    serviceId: string;
  }>();

  const { data: serviceData, isLoading: serviceLoading } =
    useCustom<CarrierServicesApiResponse>({
      url: `carrier-services`,
      method: "get",
    });

  const currentService = useMemo(() => {
    return serviceData?.data.carrierServices.find(
      (service) =>
        service.id === Number(serviceId) &&
        service.carrierId === Number(carrierId) &&
        service.countryId === Number(countryId)
    );
  }, [serviceData, serviceId]);

  const { isFetching: isExcelExportLoading, refetch: handleExcelExport } =
    useCustom({
      url: `shipment-prices/export?countryId=${countryId}&carrierId=${carrierId}&carrierServiceId=${serviceId}`,
      method: "get",
      queryOptions: {
        enabled: false,
      },
    });

  if (serviceLoading) {
    return <LinearProgress />;
  }

  return (
    <Container component={Card} sx={{ padding: 2 }} maxWidth="xl">
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        {translate("title")}
      </Typography>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            component={Card}
            display="flex"
            flexDirection="column"
            gap={2}
            padding={2}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {translateShipmentFields("country")}:
              </Typography>
              <Avatar
                src={currentService?.countryFlagUrl ?? ""}
                alt={currentService?.countryName}
                sx={{ width: 24, height: 24 }}
                variant="square"
                slotProps={{
                  img: { style: { objectFit: "contain" } },
                }}
              />
              <Typography variant="subtitle1">
                {currentService?.countryName}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {translateShipmentFields("carrier")}:
              </Typography>
              <Avatar
                src={currentService?.carrierLogoUrl ?? ""}
                alt={currentService?.carrierName}
                sx={{ width: 24, height: 24 }}
                variant="square"
                slotProps={{
                  img: { style: { objectFit: "contain" } },
                }}
              />
              <Typography variant="subtitle1">
                {currentService?.carrierName}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {translateShipmentFields("service")}:
              </Typography>
              <Typography variant="body1">{currentService?.name}</Typography>
            </Stack>
          </Box>
          <LoadingButton
            loading={isExcelExportLoading}
            variant="outlined"
            startIcon={<FileDownload />}
            color="primary"
            sx={{ width: "100%", mt: 2 }}
            onClick={() => handleExcelExport()}
          >
            {translate("exportExcel")}
          </LoadingButton>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <PriceList />
        </Grid>
      </Grid>
    </Container>
  );
};
